import React, { useState } from 'react';
import { t } from '@lingui/macro';

import SimplifiedPriceColumn from '@rover/components/src/cards/SearchResultCard/PriceColumn/SimplifiedPriceColumn';
import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Box, Flex, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import useTheme from '@rover/kibble/themes/useTheme';
import { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';

import { FeeExplanationModal } from '../../../../search/SearchPage/components/Modals/FeeExplanationModal';
import {
  PtCondensedBanner,
  PtCondensedPetTypeDescription,
} from '../../../../search/SearchPage/components/PtCondensedBanner';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { SitterPriceBanner } from '../SitterPriceBanner';

import RevampAdditionalRates from './RevampAdditionalRates';
import { CTAPropsCommon } from './RevampServicesCta';
import { serviceIconToGraphic } from './utils';

type Props = {
  handleOpenSheet: () => void;
  isOpen: boolean;
};

const RevampServicesCtaCommon = ({
  priceTransparencyLevel,
  language,
  petType,
  petCount,
  bookingFeePercentage,
  handleOpenSheet,
  isOpen,
}: CTAPropsCommon & Props): JSX.Element => {
  const theme = useTheme();
  const { sitterData, serviceType, selectedService } = useRevampCta();
  const {
    countryConfigUrls,
    isPetCareModes,
    isPtFull,
    isPtCondensed,
    displayPetType,
    petTypeServices,
    rate,
    price,
  } = useRevampCtaCommon(priceTransparencyLevel, petType, petCount, selectedService);
  const [isFeeExplanationModalOpen, setIsFeeExplanationModalOpen] = useState(false);

  type iconVariant = 'away' | 'selected' | 'dropItem';

  const renderServiceIcon = (iconKey: string, variant: iconVariant): JSX.Element | null => {
    const IconComponent = serviceIconToGraphic[iconKey];
    if (!IconComponent) return null;
    let fillColor: string;
    switch (variant) {
      case 'away':
        fillColor = theme.colors.text.disabled;
        break;
      case 'selected':
        fillColor = theme.colors.interactive_text.primary_base;
        break;
      case 'dropItem':
        fillColor = theme.colors.text.success;
        break;
      default:
        fillColor = theme.colors.interactive_text.primary_base;
    }
    return (
      <Box width="24px" height="24px" flexShrink="0">
        <IconComponent width="24" height="24" aria-hidden="true" fill={fillColor} />
      </Box>
    );
  };

  type RenderDropMenuItemVariant = 'selected' | 'dropItem';

  const renderDropMenuItem = (
    service: EnhancedPersonSitterProfileService,
    variant: RenderDropMenuItemVariant = 'dropItem'
  ): JSX.Element => {
    return (
      <>
        <Flex flexDirection="row" gap="2x">
          <Text
            size="300"
            fontWeight="semibold"
            textColor={service.isAway ? 'disabled' : 'primary'}
          >
            {service.displayName}
          </Text>
          {service.isAway && variant === 'dropItem' && <Badge text={t`Away`} variant="tertiary" />}
        </Flex>
        {service.description && (
          <Text size="200" textColor={service.isAway ? 'disabled' : 'secondary'}>
            {service.description}
          </Text>
        )}
      </>
    );
  };

  return (
    <>
      <Flex
        data-testid="revamp-service-selector-trigger"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        onPress={handleOpenSheet}
        webAs="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        border="none"
        p="2x"
        sx={{
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        {(selectedService && (
          <Flex gap="3x">
            {renderServiceIcon(selectedService.icon, selectedService.isAway ? 'away' : 'selected')}
            <Flex flexDirection="column" alignItems="flex-start">
              {renderDropMenuItem(selectedService, 'selected')}
            </Flex>
          </Flex>
        )) || <Text size="300">{t`Select a service`}</Text>}

        <Box
          width="24px"
          height="24px"
          flexShrink="0"
          sx={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
          aria-hidden="true"
        >
          <DropdownSmall
            width="24"
            height="24"
            fill={selectedService?.isAway ? theme.colors.text.disabled : theme.colors.text.primary}
          />
        </Box>
      </Flex>

      <Flex flexDirection="column" gap="2x" sx={{ paddingLeft: '44px' }}>
        {rate && price && (
          <SimplifiedPriceColumn
            currencyCode={rate.pricePerUnitObj.currencyIso}
            priceTransparencyLevel={priceTransparencyLevel}
            language={language}
            price={price}
            priceInCredits={0}
            shouldShowPriceInCredits={false}
            showFromText
            unitAsSentence={rate?.unitAsSentence}
            isBHUser={false}
            variant="cta-desktop"
          />
        )}

        {isPtCondensed && <PtCondensedPetTypeDescription petType={displayPetType} />}

        <RevampAdditionalRates
          services={sitterData.services}
          serviceType={serviceType}
          petType={petType}
          petCount={petCount}
          isSitterInPetCareModesExperiment={isPetCareModes}
        />
      </Flex>
      {isPtCondensed ? (
        <PtCondensedBanner
          priceTransparencyLevel={priceTransparencyLevel}
          bookingFeePercentage={bookingFeePercentage}
          onClick={() => setIsFeeExplanationModalOpen(true)}
          isSitterProfile
        />
      ) : (
        isPtFull && (
          <SitterPriceBanner
            petCounts={petCount}
            petType={petType}
            serviceType={serviceType}
            countryConfigUrls={countryConfigUrls}
            bookingFeePercentage={bookingFeePercentage}
            services={petTypeServices}
            version="revamp"
          />
        )
      )}
      <FeeExplanationModal
        isOpen={isFeeExplanationModalOpen}
        onClose={() => setIsFeeExplanationModalOpen(false)}
        priceTransparencyLevel={priceTransparencyLevel || PriceTransparencyLevels.lite_condensed}
        bookingPercentageFee={bookingFeePercentage}
      />
    </>
  );
};

export default RevampServicesCtaCommon;
